import { Component, computed, effect, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingSpinnerComponent } from "../../../../shared/components/loading-spinner/loading-spinner.component";
import { AddWorkspaceDialogComponent } from '../../dialogs/add-workspace-dialog/add-workspace-dialog.component';
import { Workspace } from './models/workspace.model';
import { WorkspaceService } from './services/workspace.service';
import { WorkspaceCreatingStates, WorkspaceFailureStates, WorkspaceSuccessStates } from './models/workspace-provision-status.model';
import { DataStoreProvisionStatusType } from '../data-lakes-tab/models/data-store-provision-status';
import { DataStoreService } from '../data-lakes-tab/services/data-store.service';
import { WorkspacePublicNetworkAccessType } from './models/workspace-public-network-access.model';
import { EditWorkspaceDialogComponent } from '../../dialogs/edit-workspace-dialog/edit-workspace-dialog.component';
import { DatePipe} from '@angular/common'
import { MatSort, MatSortable, MatSortModule } from '@angular/material/sort';

@Component({
  selector: 'app-workspaces-tab',
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSortModule,
    LoadingSpinnerComponent,
    DatePipe,
  ],
  templateUrl: './workspaces-tab.component.html',
  styleUrl: './workspaces-tab.component.css'
})
export class WorkspacesTabComponent {
  dataStoreService = inject(DataStoreService);
  workspaceService = inject(WorkspaceService);

  workspaceColumns: string[] = ['workspaceName', 'workspaceAlias', 'networkAccess', 'creationDate', 'provisionStatus'];
  workspacesDataSource = new MatTableDataSource<Workspace>;

  successStatusEnum = WorkspaceSuccessStates;
  failureStatusEnum = WorkspaceFailureStates;
  creatingStatusEnum = WorkspaceCreatingStates;

  networkAccessEnum = WorkspacePublicNetworkAccessType;

  private _sort!: MatSort;
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this._sort = sort;
      this.workspacesDataSource.sort = this._sort;

      // set default sort on load
      if (!this._sort.active && this.workspacesDataSource.data.length) {
        Promise.resolve().then(() => {
          this._sort.sort({ id: 'creationDate', start: 'desc' } as MatSortable);
        });
      }
    }
  }

  hasDatalake = computed(() => this.dataStoreService.dataLakes().some(datalake => datalake.provisionStatusTypeId === DataStoreProvisionStatusType.Created));
  hasLandingZone = computed(() => this.dataStoreService.landingZones().some(landingZone => landingZone.provisionStatusTypeId === DataStoreProvisionStatusType.Created));

  constructor(private dialog: MatDialog) {
    effect(() => {
      const workspaces = this.workspaceService.workspaces() as Workspace[] ?? [];
      this.workspacesDataSource.data = workspaces;
    });
  }

  ngAfterViewInit() {
    this.workspacesDataSource.sort = this._sort;

    // provide custom sorting accessor for complex properties
    this.workspacesDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'creationDate': 
          return item.createdDateTime ? new Date(item.createdDateTime).getTime() : 0;
        case 'networkAccess':
          return item.publicNetworkAccess ?? '';
        default: 
          const value = (item as any)[property];
          return typeof value === 'string' ? value.toLowerCase(): value ?? '';
      }
    };
  }

  openAddWorkspaceDialog(): void {
    const dialogRef = this.dialog.open(AddWorkspaceDialogComponent, {
      width: '560px',
      disableClose: true,
    });

    dialogRef.beforeClosed().subscribe((workspace: Workspace) => {
      if (workspace) {
        const currentWorkspaces = this.workspaceService.workspaces();
        this.workspaceService.workspaces.set([...currentWorkspaces, workspace])
      }
    });
  }

  openEditWorkspaceDialog(element: Workspace): void {
    const dialogRef = this.dialog.open(EditWorkspaceDialogComponent, {
      width: '560px',
      disableClose: true,
      data: element
    });
    
    dialogRef.beforeClosed().subscribe((updatedWorkspace: Workspace) => {
      if (updatedWorkspace) {
        const currentWorkspaces = this.workspaceService.workspaces();
        const updatedWorkspaces = currentWorkspaces.map(ws => ws.workspaceId === updatedWorkspace.workspaceId ? updatedWorkspace : ws);
        this.workspaceService.workspaces.set([...updatedWorkspaces]);
      }
    });
  }
}